import React from "react";
import { useNavigate, Link } from "react-router-dom";
import { Moon, Sun, LogOut, Terminal, ShieldCheck, Eye, Menu } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { HEADER } from "@/constants/testIds";

export default function Header({ onToggleSidebar }) {
  const { user, isAdmin, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();

  const initials = (user?.name || user?.email || "?")
    .split(/[\s@.]+/)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase())
    .join("");

  const roleLabel = user && user.role ? user.role.charAt(0).toUpperCase() + user.role.slice(1) : "";

  const handleLogout = async () => {
    await logout();
    toast.success("Signed out");
    navigate("/");
  };

  return (
    <header className="sticky top-0 z-40 backdrop-blur-md bg-background/80 border-b border-border">
      <div className="flex items-center justify-between px-4 sm:px-6 py-3">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden"
            onClick={onToggleSidebar}
            data-testid={HEADER.mobileMenu}
            aria-label="Toggle navigation"
          >
            <Menu className="h-5 w-5" />
          </Button>
          <Link to="/" data-testid={HEADER.brand} className="flex items-center gap-2.5">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary text-primary-foreground">
              <Terminal className="h-5 w-5" />
            </span>
            <span className="flex flex-col leading-none">
              <span className="font-display font-bold text-base tracking-tight">InfraPilot</span>
              <span className="text-[10px] text-muted-foreground font-mono">operational procedures</span>
            </span>
          </Link>
        </div>

        <div className="flex items-center gap-2 sm:gap-3">
          <span className="hidden sm:flex items-center gap-1.5 text-xs text-muted-foreground">
            <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
            All systems operational
          </span>

          {user && (
          <span
            data-testid={HEADER.roleBadge}
            className={`hidden xs:inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium ${
              isAdmin
                ? "border-primary/40 bg-primary/10 text-primary"
                : "border-border bg-secondary text-secondary-foreground"
            }`}
          >
            {isAdmin ? <ShieldCheck className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
            {roleLabel}
          </span>
          )}

          <Button
            variant="ghost"
            size="icon"
            onClick={toggle}
            data-testid={HEADER.themeToggle}
            aria-label="Toggle theme"
          >
            {theme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
          </Button>

          {user && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild data-testid={HEADER.userMenu}>
              <Button variant="ghost" size="icon" className="rounded-full">
                <Avatar className="h-8 w-8">
                  <AvatarFallback className="bg-secondary text-xs font-semibold">{initials}</AvatarFallback>
                </Avatar>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 bg-popover">
              <DropdownMenuLabel className="flex flex-col">
                <span className="truncate">{user?.name}</span>
                <span className="text-xs font-normal text-muted-foreground truncate">{user?.email}</span>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem data-testid={HEADER.logoutBtn} onClick={handleLogout} className="gap-2 text-red-500 focus:text-red-500">
                <LogOut className="h-4 w-4" /> Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          )}
        </div>
      </div>
    </header>
  );
}
